import MCPLogo from "@/assets/MCPLogo.svg";
import { Button } from "@/components/ui";
import { SlideUpAnimation } from "@/components/ui/animations";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { useActiveChatId } from "@/lib/hooks/useActiveChatId";
import { getMcpServersQueryOptions } from "@/lib/hooks/useMcpServers";
import { getTasksQueryOptions } from "@/lib/hooks/useTasks";
import { cn } from "@/lib/utils";
import { usePanelStore } from "@/state/stores";
import type { PanelTab } from "@/state/stores/panelStore";
import { useQueryClient } from "@tanstack/react-query";
import { CheckSquare, Edit3 } from "lucide-react";
import { memo, useCallback } from "react";
import { McpServersTab, SuggestionsTab, TasksTab } from "./tabs";

export const DrawerTabs = memo(function DrawerTabs() {
  const { setActiveTab, activeTab, isPanelOpen } = usePanelStore();
  const chatId = useActiveChatId();

  return (
    <div className="flex gap-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setActiveTab("suggestions")}
        disabled={!chatId}
        className={cn(
          "hover:bg-neutral-700/80 text-muted-foreground hover:text-foreground",
          activeTab === "suggestions" &&
            isPanelOpen &&
            "bg-neutral-700/80 text-foreground",
        )}
      >
        <Edit3 className="h-4 w-4" /> Suggestions
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => setActiveTab("mcp-servers")}
        disabled={!chatId}
        className={cn(
          "hover:bg-neutral-700/80 text-muted-foreground hover:text-foreground",
          activeTab === "mcp-servers" &&
            isPanelOpen &&
            "bg-neutral-700/80 text-foreground",
        )}
      >
        <MCPLogo className="h-3.5 w-3.5" />
        MCP Servers
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => setActiveTab("tasks")}
        disabled={!chatId}
        className={cn(
          "hover:bg-neutral-700/80 text-muted-foreground hover:text-foreground",
          activeTab === "tasks" &&
            isPanelOpen &&
            "bg-neutral-700/80 text-foreground",
        )}
      >
        <CheckSquare className="h-3.5 w-3.5" />
        Tasks
      </Button>
    </div>
  );
});

export function Drawer() {
  const { activeTab, isPanelOpen } = usePanelStore();
  const queryClient = useQueryClient();
  const activeChatId = useActiveChatId();

  const prefetchAllTabs = useCallback(() => {
    if (!activeChatId) {
      return;
    }

    queryClient.prefetchQuery(getTasksQueryOptions(activeChatId));
    queryClient.prefetchQuery(getMcpServersQueryOptions(activeChatId));
  }, [queryClient, activeChatId]);

  if (!isPanelOpen) {
    return null;
  }

  return (
    <Collapsible open={isPanelOpen}>
      <CollapsibleContent onMouseEnter={prefetchAllTabs}>
        <SlideUpAnimation key={activeTab}>
          <DrawerContent activeTab={activeTab} />
        </SlideUpAnimation>
      </CollapsibleContent>
    </Collapsible>
  );
}

const DrawerContent = ({ activeTab }: { activeTab: PanelTab }) => {
  switch (activeTab) {
    case "suggestions": {
      return <SuggestionsTab />;
    }
    case "mcp-servers": {
      return <McpServersTab />;
    }
    case "tasks": {
      return <TasksTab />;
    }
    default:
      return <SuggestionsTab />;
  }
};
